import { DestinationLocalHandlingProductCode, OriginLocalHandlingProductCode } from '@forto-dev/shipment-models'

export type LocalHandlingOption = {
  label: string
  value: string
}

export type LocalHandlingOptionsScope = 'origin_options' | 'destination_options'

// Note: i18next default namespace is `common`, so keys should be relative (no leading `common.`)
export const LOCAL_HANDLING_TRANSLATION_PREFIX = 'booking.wizard.create.extra_local_handling'

// Main carriage documentation dropdown values (as per temp_translation.json context)
export const MAIN_CARRIAGE_DOCUMENTATION_TYPES = ['COUO', 'VCO'] as const
export type MainCarriageDocumentationType = (typeof MAIN_CARRIAGE_DOCUMENTATION_TYPES)[number]

export const ORIGIN_LOCAL_HANDLING_VALUES = Object.values(OriginLocalHandlingProductCode)

type GetOriginLocalHandlingValuesInput = {
  /** Both VGM services are offered for Sea FCL bookings, in every geography */
  isVgmApplicable: boolean
}

export const getOriginLocalHandlingValues = ({ isVgmApplicable }: GetOriginLocalHandlingValuesInput) =>
  ORIGIN_LOCAL_HANDLING_VALUES.filter((value) => {
    if (value === OriginLocalHandlingProductCode.VGMO || value === OriginLocalHandlingProductCode.VGMSO) {
      return isVgmApplicable
    }

    return true
  })

// The form keeps plain codes, so compare as strings
const VGM_VALUES: string[] = [OriginLocalHandlingProductCode.VGMO, OriginLocalHandlingProductCode.VGMSO]
const VGMSO_VALUE: string = OriginLocalHandlingProductCode.VGMSO

/** VGM Submission is mandatory where Forto handles the origin, so it starts selected there */
export const withVgmSubmissionSelected = (selection: string[]): string[] =>
  selection.includes(VGMSO_VALUE) ? selection : [...selection, VGMSO_VALUE]

/** Selections made on an earlier route have to go once the VGM options are no longer offered */
export const withoutVgmSelections = (selection: string[]): string[] =>
  selection.filter((value) => !VGM_VALUES.includes(value))

export const DESTINATION_LOCAL_HANDLING_VALUES = Object.values(DestinationLocalHandlingProductCode)

const localHandlingValueToTranslationTerm = (value: string) => {
  return value.startsWith('_') ? value : value.toLowerCase()
}

export const getLocalHandlingTranslationKey = (
  scope: LocalHandlingOptionsScope,
  value: OriginLocalHandlingProductCode | DestinationLocalHandlingProductCode
) => {
  const term = localHandlingValueToTranslationTerm(value)
  return `${LOCAL_HANDLING_TRANSLATION_PREFIX}.${scope}.${term}`
}

export const buildLocalHandlingOptions = (
  translate: (key: string) => string,
  scope: LocalHandlingOptionsScope,
  values: readonly (OriginLocalHandlingProductCode | DestinationLocalHandlingProductCode)[]
): LocalHandlingOption[] => {
  return values.map((value) => {
    const key = getLocalHandlingTranslationKey(scope, value)
    return {
      value,
      label: translate(key),
    }
  })
}

export const getMainCarriageDocumentationTranslationKey = (value: string) => {
  const term = localHandlingValueToTranslationTerm(value)
  return `${LOCAL_HANDLING_TRANSLATION_PREFIX}.main_carriage_documentation.${term}`
}

export const buildMainCarriageDocumentationOptions = (
  translate: (key: string) => string,
  values: readonly string[]
): LocalHandlingOption[] => {
  return values.map((value) => {
    const key = getMainCarriageDocumentationTranslationKey(value)
    return {
      value,
      label: translate(key),
    }
  })
}
